import { Fragment, useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Checkbox, Select, Spin } from 'antd'
import PeriodoRangePicker from '../../components/PeriodoRangePicker'
import { usePeriodoCompartilhado } from '../../hooks/usePeriodoCompartilhado'
import { useFiltroCompartilhado } from '../../hooks/useFiltroCompartilhado'
import { Plus, Minus, DownloadSimple } from 'phosphor-react'
import * as XLSX from 'xlsx'
import SideMenu from '../../components/SideMenu'
import MultiFiltro from '../../components/MultiFiltro'
import api from '../../service/api'
import * as D from '../dashboard/styles'
import * as S from './styles'

const { Option } = Select

/* ---------- tipos da API ---------- */

type AjusteItem = { descricao: string; cnpjs?: string; credito: number; debito: number; total: number }
type AjusteAdm = {
  administradora: string
  cnpjs?: string
  credito: number
  debito: number
  total: number
  itens: AjusteItem[]
}
type CetLinha = { administradora: string; valorBruto: number; descontoTotal: number; cet: number }
type DetalheNo = {
  rotulo: string
  nivel: string
  valorPago: number
  prazoMedio: number
  filhos: DetalheNo[]
}

type RecData = {
  ajustes: AjusteAdm[]
  ajustesTotalCredito: number
  ajustesTotalDebito: number
  ajustesTotal: number
  cet: CetLinha[]
  cetTotalBruto: number
  cetTotalDesconto: number
  cetTotal: number
  detalhes: DetalheNo[]
  detalhesTotalPago: number
  detalhesTotalPrazo: number
  antecipacoes: Antecipacao[]
  antecipacoesTotalNegociado: number
  antecipacoesTotalDesconto: number
  antecipacoesTotalLiquido: number
}

type Antecipacao = {
  adquirente: string
  cnpj: string
  dataCredito: string
  valorNegociado: number
  desconto: number
  valorLiquido: number
}

type Filtros = { empresas: string[]; empresasCnpj: Record<string, string>; anos: number[] }

/* ---------- helpers ---------- */

const fmtBRL = (n: number) =>
  (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const money = (n: number) => (n ? fmtBRL(n) : '')
const fmtPct = (n: number) => `${(n ?? 0).toFixed(2).replace('.', ',')}%`
const fmtNum = (n: number) => (n ?? 0).toLocaleString('pt-BR')


const TODOS = 'todos'

/* ---------- componente ---------- */

const Recebimentos: React.FC = () => {
  // escopo por grupo (acesso do cliente via /acessar/:id -> ?grupo=NOME)
  const location = useLocation()
  const grupo = new URLSearchParams(location.search).get('grupo') || ''

  const [filtros, setFiltros] = useState<Filtros>({ empresas: [], empresasCnpj: {}, anos: [] })
  const [empresas, setEmpresas] = useFiltroCompartilhado<string[]>('painel.empresas', [])
  // Período (RangePicker) substitui os antigos filtros Ano/Mês/Dia.
  const [periodo, setPeriodo] = usePeriodoCompartilhado()

  const [data, setData] = useState<RecData | null>(null)
  const [loading, setLoading] = useState(false)
  const [aberto, setAberto] = useState<Record<string, boolean>>({})
  const [detAbertos, setDetAbertos] = useState<Record<string, boolean>>({})
  const [menuCsv, setMenuCsv] = useState<{ x: number; y: number } | null>(null)

  const toggle = (nome: string) =>
    setAberto((prev) => ({ ...prev, [nome]: prev[nome] === false ? true : false }))

  const toggleDet = (key: string) =>
    setDetAbertos((prev) => ({ ...prev, [key]: !prev[key] }))

  const renderNo = (no: DetalheNo, depth: number, parentKey: string): JSX.Element[] => {
    const key = `${parentKey}/${no.rotulo}`
    const temFilhos = no.filhos && no.filhos.length > 0
    const open = !!detAbertos[key]
    const linhas: JSX.Element[] = [
      <tr key={key} className={depth === 0 ? 'group' : ''}>
        <td>
          <span style={{ display: 'inline-flex', alignItems: 'center', paddingLeft: depth * 18 }}>
            {temFilhos ? (
              <span className="toggle" onClick={() => toggleDet(key)}>
                {open ? <Minus size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
              </span>
            ) : (
              <span style={{ display: 'inline-block', width: 26 }} />
            )}
            {no.rotulo}
          </span>
        </td>
        <td>{fmtBRL(no.valorPago)}</td>
        <td>{fmtNum(no.prazoMedio)}</td>
      </tr>,
    ]
    if (temFilhos && open) {
      no.filhos.forEach((f) => linhas.push(...renderNo(f, depth + 1, key)))
    }
    return linhas
  }

  useEffect(() => {
    api
      .get('/DashboardRecebimentos/get-filtros', { params: grupo ? { grupo } : {} })
      .then((res) => {
        const d = res.data?.data ?? {}
        setFiltros({ empresas: d.empresas ?? [], empresasCnpj: d.empresasCnpj ?? {}, anos: d.anos ?? [] })
      })
      .catch(() => setFiltros({ empresas: [], empresasCnpj: {}, anos: [] }))
  }, [grupo])

  const carregar = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = {}
      if (grupo) params.NomeDoGrupo = grupo
      if (empresas.length) params.Empresa = empresas.join(',')
      if (periodo?.[0]) params.DataInicio = periodo[0].format('YYYY-MM-DD')
      if (periodo?.[1]) params.DataFim = periodo[1].format('YYYY-MM-DD')

      const res = await api.get('/DashboardRecebimentos/get-dashboard-recebimentos', { params })
      setData(res.data?.data ?? null)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [grupo, empresas, periodo])

  useEffect(() => {
    carregar()
  }, [carregar])

  const ajustes = data?.ajustes ?? []
  const cet = data?.cet ?? []
  const detalhes = data?.detalhes ?? []
  const antecipacoes = data?.antecipacoes ?? []

  // baixa Custo com Antecipacoes por periodo (mes/ano) x adquirente x cnpj,
  // igual ao vendas: o Periodo real vem do backend (get-exportacao-antecipacoes)
  const baixarAntecipacoesXlsx = async () => {
    const params: Record<string, string | number> = {}
    if (grupo) params.NomeDoGrupo = grupo
    if (empresas.length) params.Empresa = empresas.join(',')
    if (periodo?.[0]) params.DataInicio = periodo[0].format('YYYY-MM-DD')
    if (periodo?.[1]) params.DataFim = periodo[1].format('YYYY-MM-DD')

    let rows: {
      periodo: string
      adquirente: string
      cnpj: string
      valorNegociado: number
      desconto: number
      valorLiquido: number
    }[] = []
    try {
      const res = await api.get('/DashboardRecebimentos/get-exportacao-antecipacoes', { params })
      rows = res.data?.data ?? []
    } catch {
      rows = []
    }

    const MOEDA = '"R$" #,##0.00'

    const aoa: (string | number)[][] = [
      ['PERÍODO', 'ADQUIRENTE', 'CNPJ', 'VALOR NEGOCIADO', 'DESCONTO', 'VALOR LÍQUIDO'],
    ]
    rows.forEach((r) =>
      aoa.push([r.periodo, r.adquirente, r.cnpj, r.valorNegociado, r.desconto, r.valorLiquido]),
    )

    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = [{ wch: 14 }, { wch: 22 }, { wch: 20 }, { wch: 18 }, { wch: 16 }, { wch: 16 }]
    for (let i = 1; i < aoa.length; i++) {
      ;['D', 'E', 'F'].forEach((c) => {
        if (ws[`${c}${i + 1}`]) ws[`${c}${i + 1}`].z = MOEDA
      })
      const cnpj = ws[`C${i + 1}`]
      if (cnpj) {
        cnpj.t = 's'
        cnpj.z = '@'
      }
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Antecipações')
    XLSX.writeFile(wb, 'custo-antecipacoes.xlsx')
  }

  return (
    <D.Page>
      <SideMenu active="recebimentos" />

      <D.Main>
        {loading && (
          <D.LoadingOverlay>
            <Spin size="large" tip="Carregando dados..." />
          </D.LoadingOverlay>
        )}

        <D.FiltersRow>
          <D.Filter>
            <label>Empresa Selecionada</label>
            <Select
              mode="multiple"
              allowClear
              value={empresas}
              onChange={setEmpresas}
              placeholder="Todos"
              maxTagCount="responsive"
              optionLabelProp="label"
              optionFilterProp="label"
              menuItemSelectedIcon={null}
              showArrow
              style={{ width: '100%' }}
            >
              {filtros.empresas.map((e) => {
                const cnpj = filtros.empresasCnpj?.[e] || 'Sem CNPJ'
                return (
                  <Option key={e} value={e} label={e} title={`${e} — CNPJ: ${cnpj}`}>
                    <span title={`CNPJ: ${cnpj}`}>
                      <Checkbox checked={empresas.includes(e)} style={{ marginRight: 8 }} />
                      {e}
                    </span>
                  </Option>
                )
              })}
            </Select>
          </D.Filter>
          <D.Filter style={{ gridColumn: 'span 2' }}>
            <label>Período</label>
            <PeriodoRangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Início', 'Fim']}
              value={periodo}
              onChange={(dates) => setPeriodo(dates as [any, any])}
              inputReadOnly
            />
          </D.Filter>
        </D.FiltersRow>

        <S.RecGrid>
          {/* Coluna esquerda: Ajustes (3/4) + Custo com Antecipações (1/4) */}
          <S.LeftCol>
            <S.RecPanel style={{ flex: 3 }}>
              <S.RecTitle>Ajustes Realizados nos Recebimentos</S.RecTitle>
            <S.TableScroll>
            <D.Table style={{ height: '100%' }}>
              <thead>
                <tr>
                  <th>Nome Administradora</th>
                  <th>Crédito</th>
                  <th>Débito</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {ajustes.map((g) => {
                  const open = aberto[g.administradora] !== false
                  return (
                    <Fragment key={g.administradora}>
                      <tr className="group">
                        <td title={g.cnpjs ? `CNPJ: ${g.cnpjs}` : undefined}>
                          <span className="toggle" onClick={() => toggle(g.administradora)}>
                            {open ? <Minus size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                          </span>
                          {g.administradora}
                        </td>
                        <td>{money(g.credito)}</td>
                        <td>{money(g.debito)}</td>
                        <td>{fmtBRL(g.total)}</td>
                      </tr>
                      {open &&
                        g.itens.map((f) => (
                          <tr className="child" key={g.administradora + f.descricao}>
                            <td title={f.cnpjs ? `CNPJ: ${f.cnpjs}` : undefined}>{f.descricao}</td>
                            <td>{money(f.credito)}</td>
                            <td>{money(f.debito)}</td>
                            <td>{fmtBRL(f.total)}</td>
                          </tr>
                        ))}
                    </Fragment>
                  )
                })}
                <tr aria-hidden="true">
                  <td colSpan={4} style={{ height: '100%', border: 'none', padding: 0 }} />
                </tr>
                <tr className="total">
                  <td>Total</td>
                  <td>{money(data?.ajustesTotalCredito ?? 0)}</td>
                  <td>{money(data?.ajustesTotalDebito ?? 0)}</td>
                  <td>{fmtBRL(data?.ajustesTotal ?? 0)}</td>
                </tr>
              </tbody>
            </D.Table>
            </S.TableScroll>
            </S.RecPanel>

            <S.RecPanel
              style={{ flex: 2 }}
              onContextMenu={(e) => {
                e.preventDefault()
                setMenuCsv({ x: e.clientX, y: e.clientY })
              }}
            >
              <S.RecTitle>Custo com Antecipações</S.RecTitle>
              <S.TableScroll>
                <D.Table style={{ height: '100%' }}>
                  <thead>
                    <tr>
                      <th>Adquirente</th>
                      <th style={{ textAlign: 'left' }}>CNPJ</th>
                      <th>Valor Negociado</th>
                      <th>Desconto</th>
                      <th>Valor Líquido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {antecipacoes.map((a, i) => (
                      <tr key={i}>
                        <td>{a.adquirente}</td>
                        <td style={{ textAlign: 'left' }}>{a.cnpj}</td>
                        <td>{fmtBRL(a.valorNegociado)}</td>
                        <td>{fmtBRL(a.desconto)}</td>
                        <td>{fmtBRL(a.valorLiquido)}</td>
                      </tr>
                    ))}
                    <tr aria-hidden="true">
                      <td colSpan={5} style={{ height: '100%', border: 'none', padding: 0 }} />
                    </tr>
                    <tr className="total">
                      <td>Total</td>
                      <td />
                      <td>{fmtBRL(data?.antecipacoesTotalNegociado ?? 0)}</td>
                      <td>{fmtBRL(data?.antecipacoesTotalDesconto ?? 0)}</td>
                      <td>{fmtBRL(data?.antecipacoesTotalLiquido ?? 0)}</td>
                    </tr>
                  </tbody>
                </D.Table>
              </S.TableScroll>
            </S.RecPanel>
          </S.LeftCol>

          {/* Coluna direita */}
          <S.RightCol>
            <S.RecPanel>
              <S.RecTitle>Custo Efetivo Total (CET)</S.RecTitle>
              <S.TableScroll>
              <D.Table style={{ height: '100%' }}>
                <thead>
                  <tr>
                    <th>Administradora</th>
                    <th>Valor Bruto</th>
                    <th>Desconto Total</th>
                    <th>CET</th>
                  </tr>
                </thead>
                <tbody>
                  {cet.map((r) => (
                    <tr key={r.administradora}>
                      <td>{r.administradora}</td>
                      <td>{fmtBRL(r.valorBruto)}</td>
                      <td>{fmtBRL(r.descontoTotal)}</td>
                      <td>{fmtPct(r.cet)}</td>
                    </tr>
                  ))}
                  <tr aria-hidden="true">
                    <td colSpan={4} style={{ height: '100%', border: 'none', padding: 0 }} />
                  </tr>
                  <tr className="total">
                    <td>Total</td>
                    <td>{fmtBRL(data?.cetTotalBruto ?? 0)}</td>
                    <td>{fmtBRL(data?.cetTotalDesconto ?? 0)}</td>
                    <td>{fmtPct(data?.cetTotal ?? 0)}</td>
                  </tr>
                </tbody>
              </D.Table>
              </S.TableScroll>
            </S.RecPanel>

            <S.RecPanel>
              <S.RecTitle>Detalhes Financeiro Realizado</S.RecTitle>
              <S.TableScroll>
              <D.Table style={{ height: '100%' }}>
                <thead>
                  <tr>
                    <th>Banco/Adm/Conta</th>
                    <th>Valor Pago</th>
                    <th>Prazo</th>
                  </tr>
                </thead>
                <tbody>
                  {detalhes.flatMap((no) => renderNo(no, 0, 'det'))}
                  <tr aria-hidden="true">
                    <td colSpan={3} style={{ height: '100%', border: 'none', padding: 0 }} />
                  </tr>
                  <tr className="total">
                    <td>Total</td>
                    <td>{fmtBRL(data?.detalhesTotalPago ?? 0)}</td>
                    <td>{fmtNum(data?.detalhesTotalPrazo ?? 0)}</td>
                  </tr>
                </tbody>
              </D.Table>
              </S.TableScroll>
            </S.RecPanel>
          </S.RightCol>
        </S.RecGrid>

        {menuCsv && (
          <>
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 998 }}
              onClick={() => setMenuCsv(null)}
              onContextMenu={(e) => {
                e.preventDefault()
                setMenuCsv(null)
              }}
            />
            <div
              style={{
                position: 'fixed',
                left: menuCsv.x,
                top: menuCsv.y,
                zIndex: 999,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                boxShadow: '0 6px 18px rgba(0,0,0,0.16)',
                padding: 4,
                minWidth: 180,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  baixarAntecipacoesXlsx()
                  setMenuCsv(null)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  border: 'none',
                  background: 'transparent',
                  color: '#4a4f57',
                  padding: '8px 10px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 14,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f2f4f7')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <DownloadSimple size={16} weight="bold" />
                Baixar XLSX
              </button>
            </div>
          </>
        )}
      </D.Main>
    </D.Page>
  )
}

export default Recebimentos
